import { useState, useCallback, useRef, useEffect } from 'react';
import { useChatMessages, type Message } from './useChatMessages';
import { useOpenAIResponses } from './useOpenAIResponses'; // OpenAI Responses API
import { useChatFormLogic } from './useChatFormLogic';
import { useChatVDPContext } from './useChatVDPContext';
import { useChatSession } from './useChatSession';
import { useAnalyticsTracking } from './useAnalyticsTracking';
import { useSessionRecovery } from './useSessionRecovery';
import { useShiftDigitalTracking } from './useShiftDigitalTracking';
import { useWaitingVideo } from './useWaitingVideo';
// Note: Message routing is now handled server-side in the edge functions
import { useStreamingIntro } from './useStreamingIntro';
import { useDealerSession } from '@/contexts/DealerSessionContext';

import { DealerManager } from '@/utils/dealerManager';
import { UserMessageEmailService } from '../utils/userMessageEmailService';
import { logger } from '../utils/logger';
import { useLanguage } from '@/contexts/LanguageContext';
import { setD2CChatbotSessionCookie } from '@/utils/cookieUtils';

export type FormType = 'Contact' | 'Finance' | 'Trade' | 'Service' | 'Offer' | 'Appointment';

export { type Message };

export const useChat = (setIsExpanded?: (expanded: boolean) => void, incompleteUserMessage?: string | null) => {
  const { t } = useLanguage();
  const { sessionData } = useDealerSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  // Use the new separated hooks
  const { 
    messages, 
    isRestoring, 
    addUserMessage, 
    addAIMessage, 
    addCarfaxMessage, 
    addErrorMessage, 
    clearMessages 
  } = useChatMessages();
  const { trackAgentMessageSent } = useAnalyticsTracking();
  
  // Session recovery for handling long inactivity periods
  const { isSessionStale, performRecovery, updateActivity } = useSessionRecovery({
    onRecoveryComplete: () => {
      logger.log('Session recovery completed, ready for messages');
    }
  });
  
  const { sendMessage: sendToOpenAI } = useOpenAIResponses();
  const { processAIResponse } = useChatFormLogic(setIsExpanded);
  const { vdpContextSent, getVDPContextMessage, markVDPContextAsSent } = useChatVDPContext();
  const { chatId, sessionId, dealerId } = useChatSession();
  
  // Shift Digital tracking
  const shiftTracking = useShiftDigitalTracking(sessionId);
  
  // Waiting video for first message
  const { waitingVideoData, fetchWaitingVideo, clearWaitingVideo, resetWaitingVideoFlag } = useWaitingVideo();
  
  // Note: Message routing is now handled server-side in the edge functions
  
  // Streaming intro for faster perceived response (disabled by default)
  const streamingIntro = useStreamingIntro();

  // Track if this is the first message
  const isFirstMessageRef = useRef(true);
  
  // Store last request for retry functionality
  const lastRequestRef = useRef<{
    content: string;
    formData?: { [key: string]: string };
    inventoryFilter?: any;
    l1Title?: string | null;
    showroomFilter?: any;
  } | null>(null);

  // Track if incomplete request was already resumed
  const incompleteResumedRef = useRef(false);

  // Hold AI responses when user is interacting with waiting video SMS form
  const holdAIResponseRef = useRef(false);
  const pendingAIResponseRef = useRef<{
    content: string;
    receivedAt?: Date;
    classification?: any | null;
    skipIntroAndClosing?: boolean;
  } | null>(null);

  // Auto-resume incomplete request when detected
  useEffect(() => {
    if (incompleteUserMessage && !incompleteResumedRef.current && !isLoading) {
      logger.log('🔄 AUTO-RESUMING incomplete request:', incompleteUserMessage);
      incompleteResumedRef.current = true;
      // Resume the request without classification since it was already done
      isFirstMessageRef.current = false;
      sendMessage(incompleteUserMessage);
    }
  }, [incompleteUserMessage, isLoading]);

  // Handle waiting video "Close" response
  useEffect(() => {
    if (waitingVideoData?.Waiting_Message === "Close") {
      logger.log('🎥 Waiting video response is "Close", minimizing chat window');
      
      // Clear waiting video first
      clearWaitingVideo();
      
      // Minimize the chat window
      if (setIsExpanded) {
        logger.log('✅ Minimizing chat window from waiting video Close');
        setIsExpanded(false);
        
        // Dispatch event for host site to detect chat minimization
        window.dispatchEvent(new CustomEvent('d2c-chat-minimized', { detail: { expanded: false } }));
      } else {
        logger.log('⚠️ setIsExpanded function not available, cannot minimize chat');
      }
    }
  }, [waitingVideoData, clearWaitingVideo, setIsExpanded]);

  // Listen for global hold/release events for AI responses (waiting video SMS form)
  useEffect(() => {
    const handleHoldEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ hold: boolean }>;
      const shouldHold = !!customEvent.detail?.hold;
      holdAIResponseRef.current = shouldHold;

      logger.log(`🔒 useChat: AI response hold ${shouldHold ? 'ENABLED' : 'DISABLED'}`);

      // When hold is released, flush any pending AI response and clear waiting video
      if (!shouldHold && pendingAIResponseRef.current) {
        const pending = pendingAIResponseRef.current;
        pendingAIResponseRef.current = null;

        (async () => {
          try {
            await addAIMessage(pending.content, pending.receivedAt, pending.classification as any, pending.skipIntroAndClosing);
            await processAIResponse(pending.content);
          } catch (error) {
            logger.error('❌ Error flushing held AI response in useChat:', error);
          } finally {
            clearWaitingVideo();
          }
        })();
      }
    };

    window.addEventListener('d2c-hold-ai-response', handleHoldEvent);
    return () => window.removeEventListener('d2c-hold-ai-response', handleHoldEvent);
  }, [addAIMessage, processAIResponse, clearWaitingVideo]);

  const sendMessage = useCallback(async (content: string, formData?: { [key: string]: string }, inventoryFilter?: any, l1Title?: string | null, showroomFilter?: any, isFromPromptMenu?: boolean) => {
    
    if (!content || !content.trim()) {
      logger.error('Empty content in sendMessage');
      return;
    }

    // Block known bot/spam messages and close chat immediately
    const trimmedContent = content.trim();
    if (trimmedContent === 'Avail') {
      logger.log('🚫 Blocked bot message "Avail" - closing chat');
      if (setIsExpanded) {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent('d2c-chat-minimized', { detail: { expanded: false, reason: 'blocked_spam' } }));
      }
      return;
    }

    // Additional guard: Block if this looks like an automatic context message without user input
    if (content.startsWith('For context') && !content.includes('User:') && !content.includes('user says:') && !content.includes('user message:')) {
      logger.error('Blocked automatic context message without user input');
      return;
    }
    
    // CRITICAL: Save to sessionStorage IMMEDIATELY and SYNCHRONOUSLY
    // This MUST happen before any async operations or state updates
    // Users can navigate within milliseconds, so we save the FULL message right here
    try {
      const existingMessages = sessionStorage.getItem('d2c_chat_messages');
      const chatMessages = existingMessages ? JSON.parse(existingMessages) : [];
      const fullMessage = { role: 'user', content: content.trim() };
      chatMessages.push(fullMessage);

      // Primary: sessionStorage (fast)
      sessionStorage.setItem('d2c_chat_messages', JSON.stringify(chatMessages));

      // Backup: localStorage (more persistent across iframe reloads)
      try {
        localStorage.setItem('d2c_chat_messages_backup', JSON.stringify(chatMessages));
      } catch {
        // Non-fatal
      }

      logger.log('💾 IMMEDIATE SAVE: User message saved to sessionStorage');
      logger.log('💾 TOTAL MESSAGES IN STORAGE:', chatMessages.length);
    } catch (error) {
      console.error('❌ Failed to immediately save message:', error);
    }

    // Store request for potential retry
    lastRequestRef.current = {
      content,
      formData,
      inventoryFilter,
      l1Title,
      showroomFilter
    };

    // Update activity tracking
    updateActivity();
    
    // Check if session recovery is needed before sending message
    if (isSessionStale) {
      logger.log('Stale session detected, performing recovery');
      const recoverySuccess = await performRecovery();
      if (!recoverySuccess) {
        logger.error('Session recovery failed, message send aborted');
        return;
      }
    }

    // Add user message and get timestamped content for OpenAI
    const userMessage = await addUserMessage(content, formData);
    setIsLoading(true);
    setIsSearching(true); // Set searching state when we start OpenAI request
    
    // Set cookie after first message
    
    if (isFirstMessageRef.current) {
      logger.log('🎥 ✅ First message detected - triggering waiting video');
      setD2CChatbotSessionCookie(sessionId);
      
      // Fetch waiting video (non-blocking)
      fetchWaitingVideo(content).catch(error => {
        console.error('🎥 Failed to fetch waiting video:', error);
      });
      
      isFirstMessageRef.current = false;
      logger.log('🎥 isFirstMessageRef set to false after first message');
    } else {
      logger.log('🎥 ❌ Not first message - skipping waiting video');
    }

    // Send user message email if dealer has it configured (skip for prompt menu messages)
    if (dealerId && sessionId && !isFromPromptMenu) {
      UserMessageEmailService.sendUserMessageEmail({
        dealerId: parseInt(dealerId, 10),
        message: content,
        sessionId,
        currentUrl: window.location.href
      }).catch(error => {
        logger.log('Failed to send user message email (non-critical):', error);
      });
    } else if (isFromPromptMenu) {
      logger.log('📧 Skipping user message email for prompt menu selection');
    }

     try {
      // Handle Carfax reports special case
      if (formData?.carfaxReport) {
        const carfaxData = JSON.parse(formData.carfaxReport);
        addCarfaxMessage(carfaxData);
        setIsLoading(false);
        setIsSearching(false);
        return;
      }
      
      // Handle form submissions - don't send to OpenAI if it's a FORM_SUBMISSION message
      if (content.startsWith('FORM_SUBMISSION:')) {
        setIsLoading(false);
        setIsSearching(false);
        return;
      }
      
      // Send content to OpenAI (timestamp handled as system message)
      const aiResponseStartTime = new Date();
      
      // Include VDP context in the first message if available
      let messageToSend = content;
      const vdpContext = getVDPContextMessage();
      if (vdpContext) {
        messageToSend = vdpContext + '\n\n' + content;
        markVDPContextAsSent();
        logger.log('VDP context included with user message');
      }
      
      let aiResponse: string | null = null;
      let wasStreamed = false; // Track if response came from streaming (intro already shown)
      let streamedMessageTimestamp: Date | undefined; // Locked timestamp from first token received
      
      // Check streaming: context first, then DealerManager cache (handles timing issues)
      const cachedDealer = DealerManager.getCurrentDealer();
      const isStreamingEnabled = streamingIntro.isStreamingEnabled() || cachedDealer?.stream === true;
      
      // Try streaming first if enabled
      if (isStreamingEnabled) {
        logger.log('🌊 [CHAT] Streaming enabled (context or cache), trying streaming endpoint...');
        logger.log('🌊 [CHAT] Streaming enabled, trying streaming endpoint...');
        const streamResult = await streamingIntro.streamMessage(
          messageToSend,
          sessionId,
          inventoryFilter,
          showroomFilter,
          l1Title
        );
        
        if (streamResult.success && streamResult.fullResponse) {
          logger.log('🌊 [CHAT] Streaming successful, using streamed response');
          aiResponse = streamResult.fullResponse;
          wasStreamed = true; // Intro was already streamed to user
          
          // Use the locked streamStartTime as the AI response timestamp (first token received)
          if (streamResult.streamStartTime) {
            streamedMessageTimestamp = streamResult.streamStartTime;
          }
          
          // CRITICAL: Also store responseId from streaming result for conversation continuity
          if (streamResult.responseId) {
            logger.log('🌊 [CHAT] Storing responseId from streaming result:', streamResult.responseId);
            try {
              localStorage.setItem('openai_response_id_direct', streamResult.responseId);
            } catch (storageError) {
              logger.warn('⚠️ [CHAT] localStorage quota exceeded storing responseId, clearing expendable data...');
              const { clearLocalStorageForQuota } = await import('@/utils/localStorageCleanup');
              clearLocalStorageForQuota();
              try {
                localStorage.setItem('openai_response_id_direct', streamResult.responseId);
              } catch { /* fallback to window only */ }
            }
            (window as any).__lastResponseId = streamResult.responseId;
          }

          // Fire Shift Digital chatLead for leads generated by send_lead tool in streaming mode
          if (Array.isArray(streamResult.generatedLeads) && streamResult.generatedLeads.length > 0) {
            try {
              const { shouldUseShiftDigitalTracking, trackShiftChatLead, determineProfitCenter } = await import('../utils/shiftDigitalTracking');
              const { DealerManager } = await import('@/utils/dealerManager');
              const dealer = DealerManager.getCurrentDealer();
              if (shouldUseShiftDigitalTracking(dealer?.certification_name)) {
                const trackedLeadIds = new Set<string>(JSON.parse(sessionStorage.getItem('d2c_shift_tracked_leads') || '[]'));
                for (const lead of streamResult.generatedLeads) {
                  const leadId = typeof lead?.leadId === 'string' ? lead.leadId : '';
                  if (!leadId || trackedLeadIds.has(leadId)) continue;
                  const profitCenter = determineProfitCenter(lead?.department);
                  const trackingSessionId = (lead?.sessionId as string) || sessionId || `temp_${Date.now()}`;
                  trackShiftChatLead(trackingSessionId, leadId, profitCenter);
                  trackedLeadIds.add(leadId);
                }
                sessionStorage.setItem('d2c_shift_tracked_leads', JSON.stringify([...trackedLeadIds]));
              }
            } catch (shiftError) {
              logger.warn('🔵 [SHIFT] streaming generated_leads tracking error (non-fatal):', shiftError);
            }
          }
        } else {
          logger.log('🌊 [CHAT] Streaming failed, falling back to regular endpoint');
          aiResponse = await sendToOpenAI(messageToSend, sessionId, inventoryFilter, l1Title, showroomFilter);
        }
      } else if (sessionData?.useMessageRouting) {
        // Force streaming ONLY for non-vehicle questions when message routing is enabled
        let shouldForceStream = false;
        let classificationResult: boolean | null = null; // Track classification result to pass to server
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          const page_title = document?.title || '';
          const dealer_domain = window?.location?.hostname || window?.location?.origin || '';
          const { data } = await supabase.functions.invoke('classify-message', {
            body: { message: messageToSend, page_title, dealer_domain }
          });
          if (data?.success) {
            classificationResult = data.is_vehicle_sales_question;
            if (data.is_vehicle_sales_question === false) {
              shouldForceStream = true;
              logger.log('🌊 [CHAT] Non-vehicle question detected - forcing streaming');
            }
          }
        } catch (e) {
          logger.warn('🌊 [CHAT] classify-message failed (continuing without forced streaming):', e);
        }

        if (shouldForceStream) {
          const streamResult = await streamingIntro.streamMessage(
            messageToSend,
            sessionId,
            inventoryFilter,
            showroomFilter,
            l1Title,
            true,
            classificationResult // Pass classification result to avoid re-classifying on server
          );
          
          if (streamResult.success && streamResult.fullResponse) {
            aiResponse = streamResult.fullResponse;
            wasStreamed = true; // Intro was already streamed to user

            // Use the locked streamStartTime as the AI response timestamp (first token received)
            if (streamResult.streamStartTime) {
              streamedMessageTimestamp = streamResult.streamStartTime;
            }
            
            // CRITICAL: Also store responseId from streaming result for conversation continuity
            if (streamResult.responseId) {
              logger.log('🌊 [CHAT] Storing responseId from forced streaming result:', streamResult.responseId);
              try {
                localStorage.setItem('openai_response_id_direct', streamResult.responseId);
              } catch (storageError) {
                logger.warn('⚠️ [CHAT] localStorage quota exceeded storing responseId, clearing expendable data...');
                const { clearLocalStorageForQuota } = await import('@/utils/localStorageCleanup');
                clearLocalStorageForQuota();
                try {
                  localStorage.setItem('openai_response_id_direct', streamResult.responseId);
                } catch { /* fallback to window only */ }
              }
              (window as any).__lastResponseId = streamResult.responseId;
            }

            // Fire Shift Digital chatLead for leads generated by send_lead tool in forced streaming mode
            if (Array.isArray(streamResult.generatedLeads) && streamResult.generatedLeads.length > 0) {
              try {
                const { shouldUseShiftDigitalTracking, trackShiftChatLead, determineProfitCenter } = await import('../utils/shiftDigitalTracking');
                const { DealerManager } = await import('@/utils/dealerManager');
                const dealer = DealerManager.getCurrentDealer();
                if (shouldUseShiftDigitalTracking(dealer?.certification_name)) {
                  const trackedLeadIds = new Set<string>(JSON.parse(sessionStorage.getItem('d2c_shift_tracked_leads') || '[]'));
                  for (const lead of streamResult.generatedLeads) {
                    const leadId = typeof lead?.leadId === 'string' ? lead.leadId : '';
                    if (!leadId || trackedLeadIds.has(leadId)) continue;
                    const profitCenter = determineProfitCenter(lead?.department);
                    const trackingSessionId = (lead?.sessionId as string) || sessionId || `temp_${Date.now()}`;
                    trackShiftChatLead(trackingSessionId, leadId, profitCenter);
                    trackedLeadIds.add(leadId);
                  }
                  sessionStorage.setItem('d2c_shift_tracked_leads', JSON.stringify([...trackedLeadIds]));
                }
              } catch (shiftError) {
                logger.warn('🔵 [SHIFT] forced streaming generated_leads tracking error (non-fatal):', shiftError);
              }
            }
          } else {
            logger.log('🌊 [CHAT] Forced streaming failed, falling back to regular endpoint');
            aiResponse = await sendToOpenAI(messageToSend, sessionId, inventoryFilter, l1Title, showroomFilter);
          }
        } else {
          aiResponse = await sendToOpenAI(messageToSend, sessionId, inventoryFilter, l1Title, showroomFilter);
        }
      } else {
        // Regular non-streaming path
        aiResponse = await sendToOpenAI(messageToSend, sessionId, inventoryFilter, l1Title, showroomFilter);
      }
      
      logger.log('AUTO-LEAD: Received AI response');
      
      // Update activity after successful response
      updateActivity();
      
      const aiResponseReceivedAt = streamedMessageTimestamp || new Date();
      
      
      if (aiResponse) {
        // CRITICAL: Check if response is EXACTLY "Close" (case-sensitive) - special case to close chat without adding message
        // This should NOT trigger if "Close" appears anywhere in a JSON response
        const trimmedResponse = aiResponse.trim().replace(/[.,!?;:]$/, '');
        const isExactCloseCommand = trimmedResponse === "Close" && !aiResponse.includes('{') && !aiResponse.includes('[');
        
        if (isExactCloseCommand) {
          logger.log('🚪 Received exact "Close" command from AI (not JSON), minimizing chat without adding message');
          clearWaitingVideo();
          if (setIsExpanded) {
            setIsExpanded(false);
            window.dispatchEvent(new CustomEvent('d2c-chat-minimized', { detail: { expanded: false } }));
          }
          setIsLoading(false);
          setIsSearching(false);
          return;
        }
        
        // ========== AUTO LEAD SUBMISSION (RUNS FIRST) ==========
        logger.log('🎯🎯🎯 [AUTO LEAD] Entering auto-lead block...');
        try {
          logger.log('🎯🎯🎯 [AUTO LEAD] Importing modules...');
          const { parseAIResponse } = await import('../utils/aiResponseParser');
          const { AutoLeadSubmission } = await import('../utils/autoLeadSubmission');
          const { sessionManager } = await import('../utils/sessionManager');
          logger.log('🎯🎯🎯 [AUTO LEAD] Modules imported successfully');
          
          logger.log('🎯🎯🎯 [AUTO LEAD] Parsing AI response...');
          const parsedResponse = parseAIResponse(aiResponse);
          
          // Store conversation summary if present
          if (parsedResponse?.Answer?.Conversation_Summary) {
            sessionManager.updateConversationSummary(parsedResponse.Answer.Conversation_Summary);
            logger.log('🎯🎯🎯 [AUTO LEAD] Conversation summary stored');
          }
          
          const hasContact = !!parsedResponse?.Contact;
          logger.log('🎯🎯🎯 [AUTO LEAD] Has Contact object?', hasContact);
          
          if (hasContact) {
            logger.log('🎯🎯🎯 [AUTO LEAD] Validating contact data...');
            const isValidContact = AutoLeadSubmission.hasValidContactData(parsedResponse.Contact);
            logger.log('🎯🎯🎯 [AUTO LEAD] Is valid contact?', isValidContact);
            
            if (isValidContact) {
              // PHONE VALIDATION: Ensure phone was provided by user and isn't a dealer phone
              const { validatePhoneForLead } = await import('../utils/phoneLeadValidation');
              const chatMessages = messages.map(m => ({ role: m.isUser ? 'user' : 'assistant', content: m.content }));
              const phoneValidation = validatePhoneForLead(parsedResponse.Contact.Phone, chatMessages);
              
              if (!phoneValidation.valid) {
                // Phone failed validation - if phone was the only contact method, skip lead entirely
                const hasEmail = !!parsedResponse.Contact.Email && parsedResponse.Contact.Email.trim().length > 0;
                if (!hasEmail) {
                  logger.log(`🎯🎯🎯 [AUTO LEAD] ❌ Skipping lead - ${phoneValidation.reason} and no email provided`);
                } else {
                  // Has email - strip the invalid phone and continue with email-only lead
                  logger.log(`🎯🎯🎯 [AUTO LEAD] ⚠️ ${phoneValidation.reason} - continuing with email only`);
                  parsedResponse.Contact.Phone = '';
                }
              }

              const hasValidContactAfterPhoneCheck = parsedResponse.Contact.Phone?.trim() || parsedResponse.Contact.Email?.trim();
              if (!hasValidContactAfterPhoneCheck) {
                logger.log('🎯🎯🎯 [AUTO LEAD] ❌ No valid contact method remaining after phone validation');
              } else {
              const currentSession = sessionManager.getCurrentSession();
              const currentSessionId = currentSession?.sessionId;
              logger.log('🎯🎯🎯 [AUTO LEAD] Current session:', currentSession);
              logger.log('🎯🎯🎯 [AUTO LEAD] Session ID:', currentSessionId);
              
              if (currentSessionId) {
                logger.log('🎯🎯🎯 [AUTO LEAD] Checking if already submitted...');
                const alreadySubmitted = await AutoLeadSubmission.hasBeenSubmitted(
                  currentSessionId,
                  parsedResponse.Contact
                );
                logger.log('🎯🎯🎯 [AUTO LEAD] Already submitted?', alreadySubmitted);
                
                if (!alreadySubmitted) {
                  logger.log('🎯🎯🎯 [AUTO LEAD] ✅ Triggering auto-lead submission...');
                  
                  const { processAutoLeadFromContact } = await import('../utils/autoLeadFromContact');
                  
                  // Merge Reply_Message.Intro and Closing into Interest data
                  const enrichedInterest = parsedResponse.Interest ? {
                    ...parsedResponse.Interest,
                    intro: parsedResponse.Reply_Message?.Intro,
                    closing: parsedResponse.Reply_Message?.Closing
                  } : {
                    intro: parsedResponse.Reply_Message?.Intro,
                    closing: parsedResponse.Reply_Message?.Closing
                  };
                  
                  logger.log('🎯🎯🎯 [AUTO LEAD] Enriched interest:', enrichedInterest);
                  logger.log('🎯🎯🎯 [AUTO LEAD] Calling processAutoLeadFromContact...');
                  
                  const result = await processAutoLeadFromContact(
                    parsedResponse.Contact,
                    enrichedInterest
                  );
                  
                  logger.log('🎯🎯🎯 [AUTO LEAD] ✅✅✅ Auto-lead submission result:', result);
                  
                  // Fire Shift Digital chatLead with real lead ID from database
                  if (result.success && result.leadId) {
                    try {
                      const { shouldUseShiftDigitalTracking, trackShiftChatLead, determineProfitCenter } = await import('../utils/shiftDigitalTracking');
                      const { DealerManager } = await import('@/utils/dealerManager');
                      const dealer = DealerManager.getCurrentDealer();
                      if (shouldUseShiftDigitalTracking(dealer?.certification_name)) {
                        const department = parsedResponse.Interest?.department;
                        const profitCenter = determineProfitCenter(department);
                        trackShiftChatLead(currentSessionId, result.leadId, profitCenter);
                        logger.log('🔵 [SHIFT] chatLead fired after auto-lead with real DB leadId:', result.leadId);
                      }
                    } catch (shiftError) {
                      logger.warn('🔵 [SHIFT] chatLead tracking error (non-fatal):', shiftError);
                    }
                  }
                } else {
                  logger.log('🎯🎯🎯 [AUTO LEAD] ❌ Skipping - already submitted in this session');
                }
              } else {
                logger.log('🎯🎯🎯 [AUTO LEAD] ❌ No session ID available');
              }
              } // end hasValidContactAfterPhoneCheck
            } else {
              logger.log('🎯🎯🎯 [AUTO LEAD] ❌ Invalid contact data - missing required fields');
            }
          } else {
            logger.log('🎯🎯🎯 [AUTO LEAD] ❌ No Contact object in response');
          }
        } catch (autoLeadError) {
          console.error('🎯🎯🎯 [AUTO LEAD] ❌❌❌ EXCEPTION:', autoLeadError);
          console.error('🎯🎯🎯 [AUTO LEAD] ❌❌❌ STACK:', autoLeadError instanceof Error ? autoLeadError.stack : 'No stack trace');
        }
        // ========== END AUTO LEAD SUBMISSION ==========
        
        // Track GA4 event immediately upon receiving AI response
        trackAgentMessageSent(aiResponse.length);
        
        // Track only user-side chatMessage events (avoid duplicate user/agent payloads)
        // shiftTracking.trackMessage(aiResponse, 'agent');
        // Don't clear waiting video immediately - keep it visible for SMS form
        // clearWaitingVideo();
        
        // Note: Message classification is now handled server-side in edge functions
        
        // If user is interacting with waiting video SMS form, hold the AI response
        if (holdAIResponseRef.current) {
          logger.log('🔒 useChat: Holding AI response until SMS form is completed');
          pendingAIResponseRef.current = {
            content: aiResponse,
            receivedAt: aiResponseReceivedAt,
            classification: null, // Classification is now handled server-side
            skipIntroAndClosing: wasStreamed, // Skip intro/closing if streamed
          };
        } else {
          // Add AI message to chat with timestamp
          // Pass wasStreamed to skip intro/closing in parsing (already shown via streaming)
          await addAIMessage(aiResponse, aiResponseReceivedAt, null, wasStreamed);
          
          // Process AI response for forms and language switching
          await processAIResponse(aiResponse);
        }
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const isRetryable = (error as any)?.isRetryable === true;
      
      // Check if this looks like a stale session error and attempt recovery
      const isStaleSessionError = errorMessage.includes('thread') || 
                                 errorMessage.includes('authentication') ||
                                 errorMessage.includes('timeout') ||
                                 errorMessage.includes('404') ||
                                 errorMessage.includes('expired');
      
      if (isStaleSessionError && !isSessionStale) {
        logger.log('Potential stale session error detected, attempting recovery');
        const recoverySuccess = await performRecovery();
        if (recoverySuccess) {
          addErrorMessage(t('error.connection.recovered'), false);
          return;
        }
      }
      
      // Handle validation errors specifically
      if (error instanceof Error && error.message === 'Unexpected error - Invalid JSON object.') {
        addErrorMessage(error.message, false);
      } else {
        // Add error message with helpful context and retry flag
        addErrorMessage(`${t('error.technical.difficulties')} ${errorMessage}`, isRetryable);
      }
    } finally {
      setIsLoading(false);
      setIsSearching(false); // Clear searching state when done

      // Only clear waiting video immediately if we're not holding the AI response for the SMS form
      if (!holdAIResponseRef.current) {
        clearWaitingVideo(); // Clear waiting video when response is complete
      } else {
        logger.log('🎥 useChat: Keeping waiting video visible while SMS form is active');
      }
    }
  }, [chatId, sessionId, addUserMessage, addAIMessage, addCarfaxMessage, addErrorMessage, sendToOpenAI, processAIResponse, trackAgentMessageSent, updateActivity, isSessionStale, performRecovery, t, dealerId, clearWaitingVideo]);

  const retryLastRequest = useCallback(async () => {
    if (!lastRequestRef.current) {
      logger.error('No request to retry');
      return;
    }
    
    const { content, formData, inventoryFilter, l1Title, showroomFilter } = lastRequestRef.current;
    
    // Re-send the message through the normal flow
    await sendMessage(content, formData, inventoryFilter, l1Title, showroomFilter);
  }, [sendMessage]);

  return {
    messages,
    isRestoring,
    isLoading,
    isSearching,
    sendMessage,
    clearMessages,
    chatId,
    sessionId,
    dealerId,
    shiftTracking,
    retryLastRequest,
    waitingVideoData,
    // Streaming intro state
    // Note: `isEnabled` is used purely for UI rendering; even if dealer-level streaming is off,
    // we still want to show the streaming UI when we force-stream a response.
    streamingIntro: {
      displayedIntro: streamingIntro.displayedIntro,
      isTyping: streamingIntro.isTyping,
      isComplete: streamingIntro.isComplete,
      isStreaming: streamingIntro.isStreaming,
      isEnabled: streamingIntro.isStreamingEnabled() || streamingIntro.isStreaming || !!streamingIntro.displayedIntro,
      streamStartTime: streamingIntro.streamStartTime,
    }
  };
};
